import React, { useEffect } from 'react';
import styled from 'styled-components';
import _ from 'lodash';
import { observer } from 'mobx-react';

import useDispensaryPaymentMethods from 'shared/hooks/use-dispensary-payment-methods';
import { useDutchiePay, useListBankAccounts } from 'components/dutchie-pay'; // TODO: move the DP hooks to src/dutchie-pay
import useStores from 'shared/hooks/use-stores';
import useLogRocket from 'src/hooks/use-logrocket';
import useTranslation from 'hooks/use-translation';
import useFeatureFlags from 'hooks/use-feature-flags';
import useDutchiePayEnrollment from 'hooks/use-dutchiePay-enrollment';
import { useRouter } from 'next/router';

import DutchiePayIcon from 'shared/assets/dutchie-pay-icon';
import SecureLockIcon from 'shared/assets/secure-lock-icon';
import { editBankAccount } from 'dutchie-pay/constants';
import { TruncateText } from 'dutchie-pay/components';
import { WarningIcon } from 'assets/warning';
import { LoadingSkeleton } from 'components/core';

const DutchiePay = ({ bankInfo = true }) => {
  const { UI, User } = useStores();
  const { t } = useTranslation();
  const FeatureFlags = useFeatureFlags();
  const paymentOptions = useDispensaryPaymentMethods();
  const DutchiePayEnrollment = useDutchiePayEnrollment();
  const router = useRouter();

  const dutchiePayPaymentOption = _.find(paymentOptions, { value: 'dutchiePay' });
  const { isEnrolledDutchiePay } = useDutchiePay();
  const { track } = useLogRocket();

  const {
    connectionStatus,
    mxWidgetUrl,
    billingAddressId,
    statusAggregatorError,
    bankAccounts,
    loading: bankAccountsLoading,
    refetchBankAccountData,
  } = useListBankAccounts({
    skip: false,
  });

  useEffect(() => {
    if (User.isLoggedIn) {
      refetchBankAccountData();
    }
    if (connectionStatus) {
      DutchiePayEnrollment.getConnectionStatusError(connectionStatus);
    }
  }, [connectionStatus, User.isLoggedIn]);

  const bankAccountData = bankAccounts.length !== 0 && bankAccounts[0];

  const editBankAccountEnabled = bankAccountData && FeatureFlags.flags['dutchiepay.edit-bankaccount'];
  const reconnectBankAccountEnabled =
    statusAggregatorError && FeatureFlags.flags['roll-out.dutchie-pay.bank-reconnection'];
  const dutchiePayPageUiEnabled = FeatureFlags.flags['rollout.dutchie-pay-page-ui'];

  const onEditButtonClick = () => {
    if (dutchiePayPageUiEnabled) {
      track(`DutchiePay_editing`);
      router.push({
        pathname: '/dutchiepay',
        query: {
          step: editBankAccount,
          ...(UI.variant === 'plus' && { refUrl: router.asPath }),
        },
      });
      DutchiePayEnrollment.editBankAccount();
    } else {
      DutchiePayEnrollment.editBankAccount();
      UI.openDutchiePaySignUpModal();
    }
  };

  const onResolveClick = () => {
    track(`DutchiePay_reconnecting`);
    router.push({
      pathname: '/dutchiepay',
    });
    DutchiePayEnrollment.reconnectBankAccount({ mxWidgetUrl, billingAddressId });
  };

  return (
    <DutchiePayWrapper>
      <IconContainer>
        <DutchiePayIcon height={19} width={98} />
        <SubLabel>{dutchiePayPaymentOption?.sublabel}</SubLabel>
      </IconContainer>
      {bankInfo && editBankAccountEnabled && isEnrolledDutchiePay && !reconnectBankAccountEnabled && (
        <DutchiePayInfoWrapper data-private>
          <SecureLockIcon />
          <DutchiePaySubTitle>
            <TruncateText maxWidth={100}>
              <DutchiePayBankName>{bankAccountData.bankName}</DutchiePayBankName>
            </TruncateText>
            <SpacedText>{t('common.accountEndingIn', 'ending in')}</SpacedText>
            {bankAccountData.lastDigits}
            <EditButton type='button' onClick={onEditButtonClick}>
              {t('common.edit', 'Edit')}
            </EditButton>
          </DutchiePaySubTitle>
        </DutchiePayInfoWrapper>
      )}
      {reconnectBankAccountEnabled && (
        <DutchiePayInfoWrapper>
          <WarningIcon />
          <DutchiePaySubTitle>
            <SpacedText>{DutchiePayEnrollment.connectionStatus}</SpacedText>
            <EditButton type='button' onClick={onResolveClick}>
              {t('common.edit', 'Resolve')}
            </EditButton>
          </DutchiePaySubTitle>
        </DutchiePayInfoWrapper>
      )}
      {bankAccountsLoading && (
        <LoadingWrapper>
          <LoadingSkeleton width='100%' height={16} />
        </LoadingWrapper>
      )}
    </DutchiePayWrapper>
  );
};
export default observer(DutchiePay);

const DutchiePayWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const IconContainer = styled.div`
  display: flex;
`;

const SubLabel = styled.span`
  padding-left: 6px;
`;

const DutchiePayInfoWrapper = styled.div`
  display: grid;
  grid-template-columns: 17px 1fr;
  align-items: center;
  padding: 7px 0 0 0;
  color: ${({ theme }) => theme.colors.grey[70]};
`;

const LoadingWrapper = styled.div`
  padding: 5px 0 0 0;
`;

const DutchiePayBankName = styled.span`
  text-transform: capitalize;
`;

export const DutchiePaySubTitle = styled.p`
  padding: 0 0 0 5px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.grey[45]};
  display: flex;
`;

export const SpacedText = styled.span`
  white-space: nowrap;

  &::before,
  &::after {
    content: ' ';
    white-space: pre;
  }
`;

const EditButton = styled.button`
  color: ${({ theme }) => theme.colors.blue[55]};
  border: 0;
  background: transparent;
  cursor: pointer;
  margin: 0 0 0 4px;
  padding: 0;
`;
